import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import { isAdmin } from '../constants/roles';
import {
  IconBack, IconShield, IconSun, IconMoon, IconUser, IconLogout, IconMenu, IconClose,
} from './ui/Icons';
import '../styles/AppHeader.css';

// Egy fejléc-gomb: desktopon 36×36 ikon-négyzet (tooltip a title-ből),
// mobilon a hamburger-menüben ikon + felirat balra rendezve.
const HeaderBtn = ({ icon, label, onClick, variant = '', className = '' }) => (
  <button
    type="button"
    className={`ah-btn${variant ? ` ah-btn--${variant}` : ''}${className ? ` ${className}` : ''}`}
    onClick={onClick}
    title={label}
    aria-label={label}
  >
    {icon}
    <span className="ah-btn-label">{label}</span>
  </button>
);

const AppHeader = ({
  siteName = 'VILLAPRESENCE',
  onOpenProfile,
  user,
  onLogout,
  onBack,               // ha van → vissza gomb jelenik meg (csak al-nézetben)
  title,               // opcionális: felülírja a logo+brand-et (pl. ügyszám+cím)
  subBar,              // opcionális: teljes szélességű 2. sor (pl. ügy meta)
  headerHidden = false,
  menuOpen: menuOpenProp,
  onMenuToggle: onMenuToggleProp,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const headerRef = useRef(null);

  // A menü lehet vezérelt (Dashboard adja a propokat) vagy önvezérelt
  // (Case / Profil oldal — belső állapotot használ).
  const [menuOpenInternal, setMenuOpenInternal] = useState(false);
  const controlled = onMenuToggleProp !== undefined;
  const menuOpen = controlled ? menuOpenProp : menuOpenInternal;
  const onMenuToggle = controlled ? onMenuToggleProp : setMenuOpenInternal;

  const [confirmLogout, setConfirmLogout] = useState(false);

  const isDark = theme === 'dark';

  // A fejléc valós magasságát publikáljuk egy CSS változóba, hogy a fix
  // fejléc alatt a tartalom pontosan kezdődjön (a kétsoros case-fejléc is).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () => {
      const h = headerHidden ? 0 : el.offsetHeight;
      document.documentElement.style.setProperty('--app-header-h', `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [headerHidden, title, subBar]);

  const doLogout = async () => {
    setConfirmLogout(false);
    sessionStorage.removeItem('dashboardSeen');
    await onLogout?.();
    navigate('/login');
  };

  const closeMenu = () => onMenuToggle?.(false);

  return (
    <header
      ref={headerRef}
      className={`app-header${headerHidden ? ' app-header--hidden' : ''}${menuOpen ? ' app-header--menu-open' : ''}${subBar ? ' app-header--has-subbar' : ''}`}
    >
      <div className="app-header-row">
        <div className="app-header-left">
          {title ? (
            <div className="app-header-context">{title}</div>
          ) : (
            <>
              <img src="/villa_presence_concierge_logo.svg" alt="" className="app-header-logo" aria-hidden="true" />
              <span className="app-header-brand">{siteName}</span>
            </>
          )}
        </div>

        {/* Mobil vezérlők: vissza gomb a hamburger mellett balra */}
        <div className="app-header-mobile-controls">
          {onBack && (
            <button
              type="button"
              className="app-header-mobile-back"
              onClick={onBack}
              aria-label={t('cases.back') || 'Back'}
              title={t('cases.back') || 'Back'}
            >
              <IconBack />
            </button>
          )}
          <button
            className="app-header-menu-toggle"
            onClick={() => onMenuToggle?.(!menuOpen)}
            aria-label={t('common.menu') || 'Menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <IconClose /> : <IconMenu />}
          </button>
        </div>

        <nav className="app-header-nav" onClick={closeMenu}>
          {onBack && (
            <HeaderBtn
              icon={<IconBack />}
              label={t('cases.back') || 'Back'}
              onClick={onBack}
              className="ah-btn--navback"
            />
          )}

          {isAdmin(user?.role) && (
            <HeaderBtn
              icon={<IconShield />}
              label={t('common.admin') || 'Admin'}
              onClick={() => navigate('/admin')}
            />
          )}

          <HeaderBtn
            icon={isDark ? <IconSun /> : <IconMoon />}
            label={isDark ? (t('profile.switchLight') || 'Light theme') : (t('profile.switchDark') || 'Dark theme')}
            onClick={() => setTheme(isDark ? 'light' : 'dark', true)}
          />

          <HeaderBtn
            icon={<IconUser />}
            label={t('profile.myProfile') || 'My Profile'}
            onClick={() => onOpenProfile?.()}
          />

          <HeaderBtn
            icon={<IconLogout />}
            label={t('common.logout') || 'Logout'}
            onClick={() => setConfirmLogout(true)}
            variant="danger"
          />
        </nav>
      </div>

      {subBar && <div className="app-header-subbar">{subBar}</div>}

      {confirmLogout && (
        <div className="ah-confirm-overlay" onClick={() => setConfirmLogout(false)}>
          <div className="ah-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="ah-confirm-text">{t('common.logoutConfirm') || 'Are you sure you want to log out?'}</p>
            <div className="ah-confirm-actions">
              <button type="button" className="ah-confirm-btn" onClick={() => setConfirmLogout(false)}>
                {t('common.no') || 'No'}
              </button>
              <button type="button" className="ah-confirm-btn ah-confirm-btn--danger" onClick={doLogout}>
                {t('common.yes') || 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default AppHeader;
